import React from "react"
import { ServicePageLayout } from "../../components/services/ServicePageLayout"
import { METADATA } from "../../components/meta/metadata"

export default function AIOpportunityPage() {
  return (
    <ServicePageLayout
      title={METADATA.aiOpportunity.title}
      metaDescription={METADATA.aiOpportunity.description}
      backgroundImage="/images/backgrounds/flickr-3052653173.jpg"
      backgroundAlt="AI Opportunity Identification"
      headline="AI Opportunity Identification:<br />Where AI Creates Genuine Value"
      tagline="Cut through the hype. Identify where AI actually makes sense for your organisation, with realistic ROI projections."
      introContent={[
        "The AI landscape is full of overblown promises and theoretical use cases. Every vendor claims their solution will transform your business. Meanwhile, leadership teams struggle to separate genuine opportunities from expensive distractions.",
        "We take a different approach. Starting from your operational reality - the pain points, inefficiencies and challenges your teams actually face - we identify where AI genuinely creates value. No speculation, no hype. Just clear-eyed analysis of what is achievable and worthwhile."
      ]}
      deliverables={[
        {
          title: "Prioritised AI opportunities",
          description: "Concrete opportunities where AI can create genuine business value in your specific context"
        },
        {
          title: "ROI analysis",
          description: "Realistic financial projections for each opportunity. We model rational value creation, not best-case fantasies"
        },
        {
          title: "Complexity ratings",
          description: "Clear Low, Medium or High complexity assessment for each opportunity, helping you understand implementation difficulty"
        },
        {
          title: "Implementation prerequisites",
          description: "What needs to be in place before each opportunity can be pursued - data, systems, knowledge, capabilities"
        },
        {
          title: "Strategic roadmap",
          description: "Sequencing recommendations showing which opportunities to pursue first and how they build on each other"
        }
      ]}
      processSteps={[
        {
          title: "Map Pain Points to Solutions",
          description: "We start with your operational challenges - identified through Operational Assessment or stakeholder engagement - and map them to potential AI solutions."
        },
        {
          title: "Assess Feasibility and Impact",
          description: "For each potential opportunity, we evaluate technical feasibility, organisational readiness and realistic impact potential."
        },
        {
          title: "Model Value Creation",
          description: "Realistic financial projections based on achievable improvements, not theoretical maximums. We build business cases that stand up to scrutiny."
        },
        {
          title: "Prioritise and Sequence",
          description: "Opportunities ranked by impact, feasibility and strategic fit. Clear recommendations on what to pursue first and why."
        }
      ]}
      benefits={[
        {
          title: "Evidence-Based Prioritisation",
          description: "Investment decisions grounded in analysis, not vendor promises or competitor anxiety. Focus resources on initiatives with genuine potential."
        },
        {
          title: "Clear Complexity Understanding",
          description: "Know what you are getting into before you commit. Complexity ratings help set realistic expectations and resource requirements."
        },
        {
          title: "Conservative Projections",
          description: "Business cases built on achievable outcomes. No inflated projections that lead to disappointment and eroded trust."
        },
        {
          title: "Foundation for Investment Decisions",
          description: "The clarity that boards and executives need to confidently approve AI investments."
        }
      ]}
      audience={[
        "Leadership teams deciding where AI creates genuine business value",
        "Boards and executives evaluating AI investment proposals",
        "Organisations with limited AI budget wanting maximum impact",
        "Teams overwhelmed by AI options seeking clarity",
        "Follow-on from Operational Assessment or standalone engagement"
      ]}
      relatedServices={[
        {
          name: "Operational Assessment",
          description: "Understand your current challenges before identifying AI opportunities",
          href: "/services/operational-assessment"
        },
        {
          name: "AI Kickstarter",
          description: "8-week programme that moves from opportunity identification to working implementation",
          href: "/services/ai-kickstarter"
        }
      ]}
      ctaHeadline="Ready to Identify Your AI Opportunities?"
      ctaDescription="Let's discuss how to cut through the hype and find where AI genuinely creates value for your organisation."
      ctaButtonText="Schedule a Consultation"
    />
  )
}
